import { router } from '@inertiajs/react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PhoneInput, parsePhone, formatFullPhone } from '@/components/ui/phone-input'
import type { PageProps } from '@/types'

interface LoginProps extends PageProps {
  step: 'request' | 'verify'
  phone: string | null
  errors: Record<string, string>
}

export default function Login({ flash, step, phone, errors: serverErrors }: LoginProps) {
  const parsed = parsePhone(phone ?? '')
  const [processing, setProcessing] = useState(false)
  const [countryCode, setCountryCode] = useState(parsed.countryCode)
  const [localPhone, setLocalPhone] = useState(parsed.localPhone)
  const [code, setCode] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setProcessing(true)
    router.post('/users/sign_in', {
      user: { phone: formatFullPhone(countryCode, localPhone), code },
    }, {
      onFinish: () => setProcessing(false),
    })
  }

  const mergedErrors = { ...serverErrors }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Bienvenido</CardTitle>
          <CardDescription>Ingresa a tu cuenta</CardDescription>
        </CardHeader>
        <CardContent>
          {flash?.notice && (
            <div className="mb-4 rounded-lg bg-green-500/10 p-3 text-sm text-green-500">
              {flash.notice}
            </div>
          )}
          {flash?.alert && (
            <div className="mb-4 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
              {flash.alert}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Teléfono (WhatsApp)</Label>
              <PhoneInput
                countryCode={countryCode}
                localPhone={localPhone}
                onCountryCodeChange={setCountryCode}
                onLocalPhoneChange={setLocalPhone}
                disabled={step === 'verify'}
              />
              {mergedErrors.phone && (
                <p className="text-sm text-destructive">{mergedErrors.phone}</p>
              )}
            </div>

            {step === 'verify' && (
              <div className="space-y-2">
                <Label htmlFor="code">Código</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
                {mergedErrors.code && (
                  <p className="text-sm text-destructive">{mergedErrors.code}</p>
                )}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={processing}>
              {processing
                ? 'Procesando...'
                : step === 'verify'
                  ? 'Verificar'
                  : 'Enviar código'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">¿No tienes cuenta? </span>
            <a href="/users/sign_up" className="text-primary hover:underline">
              Registrarse
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
